<script setup lang="ts">
  import { ref, watch, computed, reactive } from 'vue';
  import { useCoupons } from '@/composition/use-coupons';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { ICoupon } from '@/interfaces/ICoupon';
  import { debounce, throttle } from '@/utils';
  import { AlertType } from '@/interfaces/IAlert';
  import V2ModalCoupon from '@/components/modals/coupon/V2ModalCoupon.vue';
  import V2ModalCouponUsage from '@/components/modals/coupon/V2ModalCouponUsage.vue';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const couponsHelper = useCoupons();

  const coupons = ref<ICoupon[] | null>(null);
  const totalItems = ref(0);
  const activeTab = ref<'valid' | 'expired'>('valid');
  const counts = reactive({ valid: 0, expired: 0 });

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 20,
    mustSort: true,
    sortBy: ['createdAt'],
    sortDesc: [true],
    search: '',
  });

  const loading = ref(false);

  async function loadCoupons() {
    if (!restaurantStore.restaurant?.id || !restaurantStore.selectedBranch?.id)
      return;

    try {
      loading.value = true;

      const response = await couponsHelper.listCoupons({
        ...paginationData.value,
        status: activeTab.value,
      });
      coupons.value = response.items;
      totalItems.value = response.total;
      counts.valid = response.validCount;
      counts.expired = response.expiredCount;
    } catch (error) {
      // Error already handled by composable
    } finally {
      loading.value = false;
    }
  }

  const debouncedLoadCoupons = debounce(loadCoupons, 500);
  const throttledLoadCoupons = throttle(loadCoupons, 100);

  watch(
    [
      () => restaurantStore.restaurant?.id,
      () => restaurantStore.selectedBranch?.id,
    ],
    () => {
      loadCoupons();
    },
    { immediate: true }
  );

  watch(
    [paginationData, activeTab],
    ([newVal, newActiveTab], [oldVal, oldActiveTab]) => {
      if (newActiveTab !== oldActiveTab) {
        paginationData.value.page = 1;
      }

      if (newVal.search !== oldVal.search) {
        debouncedLoadCoupons();
      } else {
        throttledLoadCoupons();
      }
    },
    { deep: true }
  );

  const headers = [
    { text: 'Código', value: 'code' },
    { text: 'Nombre', value: 'name' },
    { text: 'Valor', value: 'value', sortable: false },
    { text: 'Estado', value: 'status', sortable: false },
    { text: 'Usos', value: 'usage', sortable: false },
    { text: 'Vigencia', value: 'validity', sortable: false },
    { text: 'Restricciones', value: 'restrictions', sortable: false },
    { text: 'Acciones', value: 'actions', sortable: false },
  ];

  function openNewCouponDialog() {
    modals.show(
      V2ModalCoupon,
      {},
      {
        name: 'v2-coupon',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: 800,
        width: 800,
        clickToClose: false,
      },
      {
        saved: () => {
          loadCoupons();
        },
      }
    );
  }

  function openEditCouponDialog(coupon: ICoupon) {
    modals.show(
      V2ModalCoupon,
      { couponToEdit: coupon },
      {
        name: 'v2-coupon',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: 800,
        width: 800,
        clickToClose: false,
      },
      {
        saved: () => {
          loadCoupons();
        },
      }
    );
  }

  function openUsageDialog(coupon: ICoupon) {
    modals.show(
      V2ModalCouponUsage,
      { coupon },
      {
        name: 'v2-coupon-usage',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: 700,
        width: 700,
        clickToClose: true,
      }
    );
  }

  const deletingCouponId = ref<number | null>(null);

  async function deleteCoupon(coupon: ICoupon) {
    if (deletingCouponId.value) return;

    const confirmed = await modals.confirm({
      title: 'Eliminar cupón',
      message: `¿Está seguro de que desea eliminar el cupón "${coupon.code}"?`,
      acceptText: 'Eliminar',
      cancelText: 'Cancelar',
      acceptColor: 'error',
    });

    if (!confirmed) return;

    try {
      deletingCouponId.value = coupon.id;
      const success = await couponsHelper.deleteCoupon(coupon.id);
      if (success) {
        loadCoupons();
      }
    } finally {
      deletingCouponId.value = null;
    }
  }

  async function toggleCouponStatus(coupon: ICoupon, value: boolean) {
    const updated = await couponsHelper.updateCoupon(coupon.id, {
      isActive: value,
    });

    if (updated) {
      loadCoupons();
    }
  }

  async function copyCouponCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      ui.showToast(AlertType.Success, 'Código copiado');
    } catch (error) {
      ui.showToast(AlertType.Error, 'Error al copiar código');
    }
  }

  function formatValidity(coupon: ICoupon): string {
    if (!coupon.validFrom && !coupon.validUntil) {
      return 'Sin límite';
    }

    const format = (date: string | Date | null | undefined) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
      });
    };

    const from = format(coupon.validFrom);
    const until = format(coupon.validUntil);

    if (from && until) {
      return `${from} - ${until}`;
    } else if (from) {
      return `Desde ${from}`;
    } else {
      return `Hasta ${until}`;
    }
  }

  function formatUsage(coupon: ICoupon): string {
    if (coupon.maxUses) {
      return `${coupon.currentUses}/${coupon.maxUses}`;
    }
    return `${coupon.currentUses}`;
  }

  const hasData = computed(() => coupons.value !== null);
  const isEmpty = computed(() => coupons.value?.length === 0);
</script>

<template>
  <div class="v2-coupons-page page-container">
    <div class="page-inner">
      <v-card class="elevation-0">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon>mdi-ticket-percent</v-icon>
              <span>Cupones de Descuento</span>
            </div>
            <div class="header-actions-container">
              <v-btn color="success" @click="openNewCouponDialog">
                <v-icon left>mdi-plus</v-icon>
                <span>Nuevo Cupón</span>
              </v-btn>
            </div>
          </div>
        </v-card-title>

        <v-card-subtitle>
          Administre cupones de descuento para la sucursal
          {{ restaurantStore.selectedBranch?.name || 'actual' }}
        </v-card-subtitle>

        <v-card-text>
          <div class="coupons-list">
            <div class="type-tabs">
              <v-btn
                depressed
                small
                @click="activeTab = 'valid'"
                :color="activeTab === 'valid' ? 'primary' : 'default'"
              >
                Vigentes
                <v-badge
                  text-color="primary"
                  :content="counts.valid"
                  :offset-x="-8"
                  :offset-y="-8"
                  inline
                  v-if="counts.valid > 0"
                />
              </v-btn>
              <v-btn
                depressed
                small
                @click="activeTab = 'expired'"
                :color="activeTab === 'expired' ? 'primary' : 'default'"
              >
                Vencidos o desactivados
                <v-badge
                  text-color="primary"
                  :content="counts.expired"
                  :offset-x="-8"
                  :offset-y="-8"
                  inline
                  v-if="counts.expired > 0"
                />
              </v-btn>
            </div>

            <div class="search-container">
              <v-text-field
                v-model="paginationData.search"
                prepend-inner-icon="mdi-magnify"
                label="Buscar cupón"
                placeholder="Código o nombre"
                clearable
                hide-details
                outlined
                dense
              />
            </div>

            <template v-if="!hasData">
              <div class="loading-container">
                <v-skeleton-loader type="table" />
              </div>
            </template>

            <template v-else-if="isEmpty">
              <div class="empty-state">
                <v-icon size="64" color="grey lighten-2"
                  >mdi-ticket-percent-outline</v-icon
                >
                <p class="tw-text-gray-500 tw-mt-4">No hay cupones</p>
                <v-btn
                  v-if="activeTab === 'valid'"
                  color="primary"
                  class="tw-mt-2"
                  @click="openNewCouponDialog"
                >
                  Crear primer cupón
                </v-btn>
              </div>
            </template>

            <template v-else>
              <v-data-table
                :items="coupons"
                :headers="headers"
                :options.sync="paginationData"
                :server-items-length="totalItems"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                :loading="loading"
                class="coupons-table"
              >
                <template v-slot:item.code="{ item }">
                  <div class="code-cell">
                    <code class="coupon-code">{{ item.code }}</code>
                    <v-btn icon x-small @click="copyCouponCode(item.code)">
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </template>

                <template v-slot:item.value="{ item }">
                  <span class="font-weight-medium">
                    {{ couponsHelper.formatCouponValue(item) }}
                  </span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="couponsHelper.formatCouponStatus(item).color"
                    text-color="white"
                  >
                    {{ couponsHelper.formatCouponStatus(item).text }}
                  </v-chip>
                </template>

                <template v-slot:item.usage="{ item }">
                  <v-btn
                    text
                    x-small
                    color="primary"
                    @click="openUsageDialog(item)"
                  >
                    {{ formatUsage(item) }}
                  </v-btn>
                </template>

                <template v-slot:item.validity="{ item }">
                  <span class="text-caption">
                    {{ formatValidity(item) }}
                  </span>
                </template>

                <template v-slot:item.restrictions="{ item }">
                  <div class="restrictions-cell">
                    <v-tooltip
                      v-for="(
                        restriction, index
                      ) in couponsHelper
                        .formatCouponRestrictions(item)
                        .slice(0, 2)"
                      :key="index"
                      bottom
                    >
                      <template v-slot:activator="{ on }">
                        <v-chip x-small outlined v-on="on">
                          {{ restriction.split(':')[0] }}
                        </v-chip>
                      </template>
                      <span>{{ restriction }}</span>
                    </v-tooltip>
                    <span
                      v-if="
                        couponsHelper.formatCouponRestrictions(item).length > 2
                      "
                      class="text-caption grey--text"
                    >
                      +{{
                        couponsHelper.formatCouponRestrictions(item).length - 2
                      }}
                    </span>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <div class="actions">
                    <v-switch
                      v-model="item.isActive"
                      :loading="deletingCouponId === item.id"
                      :disabled="!!deletingCouponId"
                      hide-details
                      dense
                      class="mt-0"
                      @change="toggleCouponStatus(item, $event)"
                    />

                    <v-btn icon small @click="openEditCouponDialog(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="activeTab === 'valid'"
                      icon
                      small
                      @click="deleteCoupon(item)"
                      :loading="deletingCouponId === item.id"
                      :disabled="!!deletingCouponId"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-coupons-page {
    @apply tw-flex tw-justify-center tw-p-4;

    .page-inner {
      @apply tw-w-full tw-max-w-7xl;

      .header-container {
        @apply tw-flex tw-justify-between tw-items-center tw-w-full;

        .header-title-container {
          @apply tw-flex tw-items-center tw-space-x-2;
        }
      }

      .coupons-list {
        @apply tw-space-y-4;

        .type-tabs {
          @apply tw-flex tw-space-x-2 tw-mb-4;
        }

        .search-container {
          @apply tw-mb-4 tw-max-w-md;
        }

        .loading-container {
          @apply tw-py-8;
        }

        .empty-state {
          @apply tw-text-center tw-py-16;
        }

        .coupons-table {
          .code-cell {
            @apply tw-flex tw-items-center tw-space-x-1;

            .coupon-code {
              @apply tw-font-mono tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded;
            }
          }

          .restrictions-cell {
            @apply tw-flex tw-items-center tw-gap-1 tw-flex-wrap;
          }

          .actions {
            @apply tw-flex tw-items-center tw-space-x-1;
          }
        }
      }
    }
  }
</style>
